import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  PencilIcon,
  PlusIcon,
  TrashIcon,
  NewspaperIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  BookOpenIcon,
  ClockIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { authService, AuthUser } from '../services/auth';
import { apiService } from '../services/api';
import NewsletterManager from '../components/NewsletterManager';
import MeetingNotesManager from '../components/MeetingNotesManager';
import LectioManager from '../components/LectioManager';
import GalleryManager from '../components/GalleryManager';


const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'events' | 'news' | 'team' | 'ministries' | 'reflections' | 'history' | 'massTimes' | 'devotions' | 'newsletters' | 'meetingNotes' | 'lectioDivina' | 'gallery' | 'rcia'>('events');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);


  useEffect(() => {
    checkAuthStatus();
  }, []);

  const updateScrollButtons = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) {
      return;
    }

    updateScrollButtons();
    container.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [updateScrollButtons]);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) {
      return;
    }

    const activeElement = container.querySelector<HTMLButtonElement>(`[data-tab="${activeTab}"]`);
    if (activeElement) {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeTab]);

  const scrollTabs = (direction: 'left' | 'right') => {
    const container = tabsContainerRef.current;
    if (!container) {
      return;
    }

    const scrollAmount = direction === 'left' ? -240 : 240;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const checkAuthStatus = async () => {
    try {
      console.log('Admin: Checking auth status...');
      const currentUser = await authService.getCurrentUser();
      console.log('Admin: Auth status result:', currentUser);
      setUser(currentUser);
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      console.log('Admin: Starting login process...');
      await authService.signInWithHostedUI();
      console.log('Admin: Login process completed');
    } catch (error) {
      console.error('Admin: Login failed:', error);
      alert(`Login failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const tabs = [
    { id: 'events', name: 'Events', icon: CalendarDaysIcon },
    { id: 'news', name: 'News', icon: NewspaperIcon },
    { id: 'newsletters', name: 'Newsletters', icon: NewspaperIcon },
    { id: 'meetingNotes', name: 'Meeting Notes', icon: DocumentTextIcon },
    { id: 'lectioDivina', name: 'Lectio Divina', icon: BookOpenIcon },
    { id: 'rcia', name: 'RCIA', icon: BookOpenIcon },
    { id: 'gallery', name: 'Gallery', icon: NewspaperIcon },
    { id: 'team', name: 'Team', icon: UserGroupIcon },
    { id: 'ministries', name: 'Ministries', icon: UserGroupIcon },
    { id: 'reflections', name: 'Reflections', icon: BookOpenIcon },
    { id: 'history', name: 'History', icon: ClockIcon },
    { id: 'massTimes', name: 'Mass Times', icon: ClockIcon },
    { id: 'devotions', name: 'Devotions', icon: BookOpenIcon }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-church-light flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-church-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user?.isAuthenticated) {
    return (
      <div className="min-h-screen bg-church-light flex items-center justify-center py-12">
        <div className="max-w-md w-full mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-serif font-bold text-church-dark">
                Church Admin Login
              </h1>
              <p className="text-gray-600 mt-2">
                Please log in to manage church content
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleLogin}
                className="w-full bg-church-blue hover:bg-blue-800 text-white font-semibold py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
              >
                Sign In with Church Account
              </button>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Admin Access:</strong><br />
                Click the button above to sign in with your church admin account through our secure login portal.
              </p>
            </div>


          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-church-light py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-serif font-bold text-church-dark">
                Church Administration
              </h1>
              <p className="text-gray-600 mt-1">
                Manage church content and information
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Sign Out
            </button>
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <div className="relative">
            <button
              type="button"
              onClick={() => scrollTabs('left')}
              className={`absolute left-0 top-1/2 -translate-y-1/2 bg-white shadow-md rounded-full p-1 text-church-blue focus:outline-none focus:ring-2 focus:ring-church-blue transition-opacity ${
                canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              aria-label="Scroll tabs left"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>

            <div className="overflow-hidden px-8">
              <nav
                ref={tabsContainerRef}
                className="-mb-px flex space-x-6 overflow-x-auto scroll-smooth"
                role="tablist"
                aria-label="Admin sections"
              >
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      data-tab={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`flex items-center flex-shrink-0 py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 focus:outline-none ${
                        activeTab === tab.id
                          ? 'border-church-blue text-church-blue'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-5 w-5 mr-2" />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>

            <button
              type="button"
              onClick={() => scrollTabs('right')}
              className={`absolute right-0 top-1/2 -translate-y-1/2 bg-white shadow-md rounded-full p-1 text-church-blue focus:outline-none focus:ring-2 focus:ring-church-blue transition-opacity ${
                canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              aria-label="Scroll tabs right"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {activeTab === 'events' && <EventsAdmin />}
          {activeTab === 'news' && <NewsAdmin />}
          {activeTab === 'newsletters' && <NewsletterManager />}
          {activeTab === 'meetingNotes' && <MeetingNotesManager />}
          {activeTab === 'lectioDivina' && <LectioManager />}
          {activeTab === 'rcia' && <RciaAdmin />}
          {activeTab === 'gallery' && <GalleryManager />}
          {activeTab === 'team' && <TeamAdmin />}
          {activeTab === 'ministries' && <MinistriesAdmin />}
          {activeTab === 'reflections' && <ReflectionsAdmin />}
          {activeTab === 'history' && <HistoryAdmin />}
          {activeTab === 'massTimes' && <MassTimesAdmin />}
          {activeTab === 'devotions' && <DevotionsAdmin />}
        </div>
      </div>
    </div>
  );
};

// Admin Components for each section
const EventsAdmin: React.FC = () => {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await apiService.getEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await apiService.deleteEvent(id);
        await fetchEvents();
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  const handleSave = async (event: any) => {
    try {
      if (event.id) {
        await apiService.updateEvent(event.id, event);
      } else {
        await apiService.createEvent(event);
      }
      await fetchEvents();
      setEditingEvent(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-blue"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-church-dark">Manage Events</h2>
        <button 
          onClick={() => setShowAddForm(true)}
          className="bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded flex items-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Event
        </button>
      </div>
      
      <div className="space-y-4">
        {events.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No events found. Add your first event!</p>
        ) : (
          events.map((event) => (
            <div key={event.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-church-dark">{event.title}</h3>
                <p className="text-sm text-gray-500">{event.date} at {event.time}</p>
                <p className="text-sm text-gray-400">{event.location}</p>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setEditingEvent(event)}
                  className="text-church-blue hover:text-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-church-blue"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button 
                  onClick={() => handleDelete(event.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {(showAddForm || editingEvent) && (
        <EventForm
          event={editingEvent}
          onSave={handleSave}
          onCancel={() => {
            setEditingEvent(null);
            setShowAddForm(false);
          }}
        />
      )}
    </div>
  );
};

const NewsAdmin: React.FC = () => {
  const [news, setNews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingNews, setEditingNews] = useState<any>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const data = await apiService.getNews();
      setNews(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this news item?')) {
      try {
        await apiService.deleteNews(id);
        await fetchNews();
      } catch (error) {
        console.error('Error deleting news:', error);
      }
    }
  };

  const handleSave = async (newsItem: any) => {
    try {
      if (newsItem.id) {
        await apiService.updateNews(newsItem.id, newsItem);
      } else {
        await apiService.createNews(newsItem);
      }
      await fetchNews();
      setEditingNews(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving news:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-blue"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-church-dark">Manage News & Notices</h2>
        <button 
          onClick={() => setShowAddForm(true)}
          className="bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded flex items-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add News
        </button>
      </div>
      
      <div className="space-y-4">
        {news.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No news items found. Add your first news item!</p>
        ) : (
          news.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-church-dark">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.date} by {item.author}</p>
                <p className="text-sm text-gray-400">{item.category}</p>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setEditingNews(item)}
                  className="text-church-blue hover:text-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-church-blue"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button 
                  onClick={() => handleDelete(item.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {(showAddForm || editingNews) && (
        <NewsForm
          news={editingNews}
          onSave={handleSave}
          onCancel={() => {
            setEditingNews(null);
            setShowAddForm(false);
          }}
        />
      )}
    </div>
  );
};

const TeamAdmin: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState<any>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const team = await apiService.getTeam();
      setTeamMembers(team);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        await apiService.deleteTeamMember(id);
        await fetchTeamMembers();
      } catch (error) {
        console.error('Error deleting team member:', error);
      }
    }
  };

  const handleSave = async (member: any, photoFile?: File) => {
    try {
      if (photoFile) {
        const photoUrl = await apiService.uploadFile(photoFile, 'team-photos');
        member.photoUrl = photoUrl;
      }
      
      if (member.id) {
        await apiService.updateTeamMember(member.id, member);
      } else {
        await apiService.createTeamMember(member);
      }
      await fetchTeamMembers();
      setEditingMember(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Failed to save team member. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-blue"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-church-dark">Manage Parish Team</h2>
        <button 
          onClick={() => setShowAddForm(true)}
          className="bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded flex items-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Member
        </button>
      </div>
      
      <div className="space-y-4">
        {teamMembers.map((member) => (
          <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h3 className="font-medium text-church-dark">{member.name}</h3>
              <p className="text-sm text-gray-500">{member.role}</p>
              {member.email && <p className="text-sm text-gray-400">{member.email}</p>}
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => setEditingMember(member)}
                className="text-church-blue hover:text-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-church-blue"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button 
                onClick={() => handleDelete(member.id)}
                className="text-red-600 hover:text-red-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Form Modal */}
      {(showAddForm || editingMember) && (
        <TeamMemberForm
          member={editingMember}
          onSave={handleSave}
          onCancel={() => {
            setEditingMember(null);
            setShowAddForm(false);
          }}
        />
      )}
    </div>
  );
};

const ReflectionsAdmin: React.FC = () => {
  const [reflections, setReflections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingReflection, setEditingReflection] = useState<any>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchReflections();
  }, []);

  const fetchReflections = async () => {
    try {
      setLoading(true);
      const data = await apiService.getReflections();
      setReflections(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching reflections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this reflection?')) {
      try {
        await apiService.deleteReflection(id);
        await fetchReflections();
      } catch (error) {
        console.error('Error deleting reflection:', error);
      }
    }
  };

  const handleSave = async (reflection: any) => {
    try {
      if (reflection.id) {
        await apiService.updateReflection(reflection.id, reflection);
      } else {
        await apiService.createReflection(reflection);
      }
      await fetchReflections();
      setEditingReflection(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving reflection:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-blue"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-church-dark">Manage Reflections</h2>
        <button 
          onClick={() => setShowAddForm(true)}
          className="bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded flex items-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Reflection
        </button>
      </div>
      
      <div className="space-y-4">
        {reflections.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No reflections found. Add your first reflection!</p>
        ) : (
          reflections.map((reflection) => (
            <div key={reflection.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-church-dark">{reflection.title}</h3>
                <p className="text-sm text-gray-500">{reflection.date}</p>
                <p className="text-sm text-gray-400">{reflection.excerpt}</p>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setEditingReflection(reflection)}
                  className="text-church-blue hover:text-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-church-blue"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button 
                  onClick={() => handleDelete(reflection.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {(showAddForm || editingReflection) && (
        <ReflectionForm
          reflection={editingReflection}
          onSave={handleSave}
          onCancel={() => {
            setEditingReflection(null);
            setShowAddForm(false);
          }}
        />
      )}
    </div>
  );
};

const HistoryAdmin: React.FC = () => {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingHistory, setEditingHistory] = useState<any>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await apiService.getHistory();
      setHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this history entry?')) {
      try {
        await apiService.deleteHistoryEntry(id);
        await fetchHistory();
      } catch (error) {
        console.error('Error deleting history:', error);
      }
    }
  };

  const handleSave = async (historyItem: any) => {
    try {
      if (historyItem.id) {
        await apiService.updateHistoryEntry(historyItem.id, historyItem);
      } else {
        await apiService.createHistoryEntry(historyItem);
      }
      await fetchHistory();
      setEditingHistory(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving history:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-blue"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-church-dark">Manage Church History</h2>
        <button 
          onClick={() => setShowAddForm(true)}
          className="bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded flex items-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add History Entry
        </button>
      </div>
      
      <div className="space-y-4">
        {history.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No history entries found. Add your first entry!</p>
        ) : (
          history.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-church-dark">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.year} - {item.period}</p>
                <p className="text-sm text-gray-400">{item.excerpt}</p>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setEditingHistory(item)}
                  className="text-church-blue hover:text-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-church-blue"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button 
                  onClick={() => handleDelete(item.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {(showAddForm || editingHistory) && (
        <HistoryForm
          history={editingHistory}
          onSave={handleSave}
          onCancel={() => {
            setEditingHistory(null);
            setShowAddForm(false);
          }}
        />
      )}
    </div>
  );
};

const MassTimesAdmin: React.FC = () => {
  const [massTimes, setMassTimes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ day: '', times: '', special: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ day: '', times: '', special: '' });

  useEffect(() => {
    loadMassTimes();
  }, []);

  const loadMassTimes = async () => {
    try {
      const data = await apiService.getMassTimes();
      setMassTimes(data);
    } catch (error) {
      console.error('Error loading mass times:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (massTime: any) => {
    setEditingId(massTime.id);
    setEditForm({
      day: massTime.day,
      times: massTime.times,
      special: massTime.special || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ day: '', times: '', special: '' });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    
    try {
      await apiService.updateMassTime(editingId, editForm);
      await loadMassTimes();
      setEditingId(null);
      setEditForm({ day: '', times: '', special: '' });
    } catch (error) {
      console.error('Error updating mass time:', error);
      alert('Failed to update mass time. Please try again.');
    }
  };

  const handleAdd = async () => {
    if (!addForm.day.trim() || !addForm.times.trim()) {
      alert('Please fill in both day and times fields.');
      return;
    }

    try {
      await apiService.createMassTime(addForm);
      await loadMassTimes();
      setShowAddForm(false);
      setAddForm({ day: '', times: '', special: '' });
    } catch (error) {
      console.error('Error adding mass time:', error);
      alert('Failed to add mass time. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this mass time?')) {
      try {
        await apiService.deleteMassTime(id);
        await loadMassTimes();
      } catch (error) {
        console.error('Error deleting mass time:', error);
        alert('Failed to delete mass time. Please try again.');
      }
    }
  };

  const initializeDefaults = async () => {
    try {
      await apiService.initializeMassTimes();
      await loadMassTimes();
      alert('Default mass times initialized successfully!');
    } catch (error) {
      console.error('Error initializing mass times:', error);
      alert('Failed to initialize mass times. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-blue mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading mass times...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-church-dark">Manage Mass Times</h2>
        <div className="flex space-x-2">
          {massTimes.length < 7 && (
            <button 
              onClick={() => setShowAddForm(true)}
              className="bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded flex items-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Mass Time
            </button>
          )}
          <button 
            onClick={initializeDefaults}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded flex items-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Initialize Defaults
          </button>
        </div>
      </div>
      
      <div className="space-y-4">
        {massTimes.map((massTime) => (
          <div key={massTime.id} className="p-4 border border-gray-200 rounded-lg">
            {editingId === massTime.id ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                  <input
                    type="text"
                    value={editForm.day}
                    onChange={(e) => setEditForm({ ...editForm, day: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Times</label>
                  <input
                    type="text"
                    value={editForm.times}
                    onChange={(e) => setEditForm({ ...editForm, times: e.target.value })}
                    placeholder="e.g., 8:00 AM, 10:30 AM, 6:00 PM"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Special Note (optional)</label>
                  <input
                    type="text"
                    value={editForm.special}
                    onChange={(e) => setEditForm({ ...editForm, special: e.target.value })}
                    placeholder="e.g., Children's Mass at 10:30 AM"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={saveEdit}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm"
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-church-dark">{massTime.day}</h3>
                  <p className="text-sm text-gray-600">{massTime.times}</p>
                  {massTime.special && (
                    <p className="text-sm text-church-gold italic">{massTime.special}</p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => startEdit(massTime)}
                    className="text-church-blue hover:text-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-church-blue"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(massTime.id)}
                    className="text-red-600 hover:text-red-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Mass Time</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                <input
                  type="text"
                  value={addForm.day}
                  onChange={(e) => setAddForm({ ...addForm, day: e.target.value })}
                  placeholder="e.g., Saturday, Sunday, Weekdays"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Times</label>
                <input
                  type="text"
                  value={addForm.times}
                  onChange={(e) => setAddForm({ ...addForm, times: e.target.value })}
                  placeholder="e.g., 8:00 AM, 10:30 AM, 6:00 PM"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Special Note (optional)</label>
                <input
                  type="text"
                  value={addForm.special}
                  onChange={(e) => setAddForm({ ...addForm, special: e.target.value })}
                  placeholder="e.g., Children's Mass at 10:30 AM"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                />
              </div>
              <div className="flex space-x-2 pt-4">
                <button
                  onClick={handleAdd}
                  className="flex-1 bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setAddForm({ day: '', times: '', special: '' });
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <h3 className="text-sm font-semibold text-blue-800 mb-2">Instructions:</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Click "Add Mass Time" to add new mass times (up to 7 total)</li>
          <li>• Click the edit icon to modify existing mass times</li>
          <li>• Click the trash icon to delete mass times</li>
          <li>• Times should be formatted like: "8:00 AM, 10:30 AM, 6:00 PM"</li>
          <li>• Changes will appear immediately on the website</li>
          <li>• Use "Initialize Defaults" to reset to standard Catholic mass times</li>
        </ul>
      </div>
    </div>
  );
};

const TeamMemberForm: React.FC<{
  member: any;
  onSave: (member: any, photoFile?: File) => void;
  onCancel: () => void;
}> = ({ member, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: member?.name || '',
    role: member?.role || '',
    bio: member?.bio || '',
    email: member?.email || '',
    phone: member?.phone || '',
    photoUrl: member?.photoUrl || ''
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setPhotoFile(file);
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setFormData({ ...formData, photoUrl: previewUrl });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: member?.id
    }, photoFile || undefined);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {member ? 'Edit Team Member' : 'Add Team Member'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <input
              type="text"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={3}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
            <div className="space-y-2">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              />
              {formData.photoUrl && (
                <div className="mt-2">
                  <img 
                    src={formData.photoUrl} 
                    alt="Preview" 
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <p className="text-xs text-gray-500 mt-1">{photoFile ? 'New photo selected' : 'Current photo'}</p>
                </div>
              )}
              <input
                type="url"
                value={formData.photoUrl}
                onChange={(e) => setFormData({ ...formData, photoUrl: e.target.value })}
                placeholder="Or enter photo URL directly"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue text-sm"
              />
            </div>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EventForm: React.FC<{
  event: any;
  onSave: (event: any) => void;
  onCancel: () => void;
}> = ({ event, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: event?.title || '',
    description: event?.description || '',
    date: event?.date || new Date().toISOString().split('T')[0],
    time: event?.time || '',
    location: event?.location || '',
    category: event?.category || 'ministry'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: event?.id
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {event ? 'Edit Event' : 'Add Event'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={3}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
            <input
              type="text"
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              placeholder="e.g., 7:00 PM"
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
            >
              <option value="ministry">Ministry</option>
              <option value="social">Social</option>
              <option value="education">Education</option>
              <option value="special">Special</option>
              <option value="mass">Mass</option>
            </select>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NewsForm: React.FC<{
  news: any;
  onSave: (news: any) => void;
  onCancel: () => void;
}> = ({ news, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: news?.title || '',
    content: news?.content || '',
    date: news?.date || new Date().toISOString().split('T')[0],
    author: news?.author || 'Parish Office',
    category: news?.category || 'announcement'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: news?.id
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {news ? 'Edit News' : 'Add News'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={4}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
            <input
              type="text"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
            >
              <option value="announcement">Announcement</option>
              <option value="newsletter">Newsletter</option>
              <option value="community">Community</option>
              <option value="ministry">Ministry</option>
            </select>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReflectionForm: React.FC<{
  reflection: any;
  onSave: (reflection: any) => void;
  onCancel: () => void;
}> = ({ reflection, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: reflection?.title || '',
    content: reflection?.content || '',
    excerpt: reflection?.excerpt || '',
    date: reflection?.date || new Date().toISOString().split('T')[0]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: reflection?.id
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {reflection ? 'Edit Reflection' : 'Add Reflection'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={2}
              placeholder="Brief summary or opening line"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Content</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={6}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const HistoryForm: React.FC<{
  history: any;
  onSave: (history: any) => void;
  onCancel: () => void;
}> = ({ history, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: history?.title || '',
    year: history?.year || '',
    period: history?.period || '',
    content: history?.content || '',
    excerpt: history?.excerpt || '',
    imageUrl: history?.imageUrl || '',
    order: history?.order ?? 0
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: history?.id
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {history ? 'Edit History Entry' : 'Add History Entry'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input
                type="text"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                placeholder="e.g., 1855"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
              <input
                type="text"
                value={formData.period}
                onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                placeholder="e.g., Victorian Era"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={2}
              placeholder="Brief summary"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Content</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={4}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              placeholder="0 = earliest, higher numbers = later"
              min="0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image URL (optional)</label>
            <input
              type="url"
              value={formData.imageUrl}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              placeholder="https://example.com/image.jpg"
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DevotionsAdmin: React.FC = () => {
  const [devotions, setDevotions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingDevotion, setEditingDevotion] = useState<any>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchDevotions();
  }, []);

  const fetchDevotions = async () => {
    try {
      setLoading(true);
      const data = await apiService.getDevotions();
      setDevotions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching devotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this devotion?')) {
      try {
        await apiService.deleteDevotion(id);
        await fetchDevotions();
      } catch (error) {
        console.error('Error deleting devotion:', error);
      }
    }
  };

  const handleSave = async (devotion: any) => {
    try {
      if (devotion.id) {
        await apiService.updateDevotion(devotion.id, devotion);
      } else {
        await apiService.createDevotion(devotion);
      }
      await fetchDevotions();
      setEditingDevotion(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving devotion:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-blue"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-church-dark">Manage Devotions</h2>
        <button 
          onClick={() => setShowAddForm(true)}
          className="bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded flex items-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Devotion
        </button>
      </div>
      
      <div className="space-y-4">
        {devotions.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No devotions found. Add your first devotion!</p>
        ) : (
          devotions.map((devotion) => (
            <div key={devotion.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-church-dark">{devotion.name}</h3>
                <p className="text-sm text-gray-500">
                  {devotion.day}
                  {devotion.time && ` at ${devotion.time}`}
                </p>
                {devotion.note && (
                  <p className="text-sm text-gray-400 italic">{devotion.note}</p>
                )}
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setEditingDevotion(devotion)}
                  className="text-church-blue hover:text-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-church-blue"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button 
                  onClick={() => handleDelete(devotion.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {(showAddForm || editingDevotion) && (
        <DevotionForm
          devotion={editingDevotion}
          onSave={handleSave}
          onCancel={() => {
            setEditingDevotion(null);
            setShowAddForm(false);
          }}
        />
      )}
    </div>
  );
};

const DevotionForm: React.FC<{
  devotion: any;
  onSave: (devotion: any) => void;
  onCancel: () => void;
}> = ({ devotion, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: devotion?.name || '',
    day: devotion?.day || 'Sunday',
    time: devotion?.time || '',
    note: devotion?.note || ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: devotion?.id
    });
  };

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">
          {devotion ? 'Edit Devotion' : 'Add Devotion'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Devotion Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Rosary, Adoration, Stations of the Cross"
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
            <select
              value={formData.day}
              onChange={(e) => setFormData({ ...formData, day: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            >
              {daysOfWeek.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time (optional)</label>
            <input
              type="text"
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              placeholder="e.g., 7:00 PM, After Mass"
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Special Note (optional)</label>
            <textarea
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              placeholder="e.g., Bring your own rosary, All welcome"
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={2}
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MinistriesAdmin: React.FC = () => {
  const [ministries, setMinistries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingMinistry, setEditingMinistry] = useState<any>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchMinistries();
  }, []);

  const fetchMinistries = async () => {
    try {
      setLoading(true);
      const data = await apiService.getMinistries();
      setMinistries(data);
    } catch (error) {
      console.error('Error fetching ministries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this ministry?')) {
      try {
        await apiService.deleteMinistry(id);
        await fetchMinistries();
      } catch (error) {
        console.error('Error deleting ministry:', error);
        alert('Failed to delete ministry. Please try again.');
      }
    }
  };

  const handleSave = async (ministry: any, photoFile?: File) => {
    try {
      setSaving(true);
      if (photoFile) {
        const photoUrl = await apiService.uploadFile(photoFile, 'ministry-contacts');
        ministry.contactPhotoUrl = photoUrl;
      }
      
      if (ministry.id) {
        await apiService.updateMinistry(ministry.id, ministry);
      } else {
        await apiService.createMinistry(ministry);
      }
      await fetchMinistries();
      setEditingMinistry(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving ministry:', error);
      alert('Failed to save ministry. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-blue"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-church-dark">Manage Ministries</h2>
        <button 
          onClick={() => setShowAddForm(true)}
          className="bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded flex items-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Ministry
        </button>
      </div>
      
      <div className="space-y-4">
        {ministries.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No ministries found. Add your first ministry!</p>
        ) : (
          ministries.map((ministry) => (
            <div key={ministry.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-church-dark">{ministry.name}</h3>
                <p className="text-sm text-gray-500">{ministry.description.substring(0, 100)}...</p>
                {ministry.contactName && (
                  <p className="text-sm text-gray-400">Contact: {ministry.contactName}</p>
                )}
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setEditingMinistry(ministry)}
                  className="text-church-blue hover:text-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-church-blue"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button 
                  onClick={() => handleDelete(ministry.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {(showAddForm || editingMinistry) && (
        <MinistryForm
          ministry={editingMinistry}
          onSave={handleSave}
          saving={saving}
          onCancel={() => {
            setEditingMinistry(null);
            setShowAddForm(false);
          }}
        />
      )}
    </div>
  );
};

const MinistryForm: React.FC<{
  ministry: any;
  onSave: (ministry: any, photoFile?: File) => void;
  saving: boolean;
  onCancel: () => void;
}> = ({ ministry, onSave, saving, onCancel }) => {
  const [formData, setFormData] = useState({
    name: ministry?.name || '',
    description: ministry?.description || '',
    contactName: ministry?.contactName || '',
    contactEmail: ministry?.contactEmail || '',
    contactPhone: ministry?.contactPhone || '',
    contactPhotoUrl: ministry?.contactPhotoUrl || '',
    order: ministry?.order || 0
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setPhotoFile(file);
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setFormData({ ...formData, contactPhotoUrl: previewUrl });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: ministry?.id
    }, photoFile || undefined);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {ministry ? 'Edit Ministry' : 'Add Ministry'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ministry Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={3}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              min="0"
            />
          </div>

          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-700 mb-3">Contact Person (Optional)</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Photo</label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
                  />
                  {formData.contactPhotoUrl && (
                    <div className="mt-2">
                      <img 
                        src={formData.contactPhotoUrl} 
                        alt="Preview" 
                        className="w-20 h-20 object-cover rounded border"
                      />
                      <p className="text-xs text-gray-500 mt-1">{photoFile ? 'New photo selected' : 'Current photo'}</p>
                    </div>
                  )}
                  <input
                    type="url"
                    value={formData.contactPhotoUrl}
                    onChange={(e) => setFormData({ ...formData, contactPhotoUrl: e.target.value })}
                    placeholder="Or enter photo URL directly"
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RciaAdmin: React.FC = () => {
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSession, setEditingSession] = useState<any>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const data = await apiService.getRcia();
      setSessions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching RCIA sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this RCIA session?')) {
      try {
        await apiService.deleteRciaSession(id);
        await fetchSessions();
      } catch (error) {
        console.error('Error deleting RCIA session:', error);
      }
    }
  };

  const handleSave = async (session: any) => {
    try {
      if (session.id) {
        await apiService.updateRciaSession(session.id, session);
      } else {
        await apiService.createRciaSession(session);
      }
      await fetchSessions();
      setEditingSession(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving RCIA session:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-blue"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-church-dark">Manage RCIA Sessions</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded flex items-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Session
        </button>
      </div>

      <div className="space-y-4">
        {sessions.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No RCIA sessions found. Add the first session summary!</p>
        ) : (
          sessions.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-church-dark">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.date} by {item.author}</p>
                {item.sessionNumber && (
                  <p className="text-sm text-gray-400">Session {item.sessionNumber}</p>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingSession(item)}
                  className="text-church-blue hover:text-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-church-blue"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {(showAddForm || editingSession) && (
        <RciaForm
          session={editingSession}
          onSave={handleSave}
          onCancel={() => {
            setEditingSession(null);
            setShowAddForm(false);
          }}
        />
      )}
    </div>
  );
};

const RciaForm: React.FC<{
  session: any;
  onSave: (session: any) => void;
  onCancel: () => void;
}> = ({ session, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: session?.title || '',
    content: session?.content || '',
    date: session?.date || new Date().toISOString().split('T')[0],
    author: session?.author || 'Parish',
    sessionNumber: session?.sessionNumber || ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      sessionNumber: formData.sessionNumber ? Number(formData.sessionNumber) : null,
      id: session?.id
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {session ? 'Edit RCIA Session' : 'Add RCIA Session'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              placeholder="e.g., Summary of RCIA Session 10 on 27 January 2026"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Session Number</label>
              <input
                type="number"
                value={formData.sessionNumber}
                onChange={(e) => setFormData({ ...formData, sessionNumber: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
                placeholder="e.g., 10"
                min="1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Author *</label>
            <input
              type="text"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={10}
              placeholder="Paste the full session summary here..."
              required
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Admin;
